import { FabricCanvas, FabricPoint } from "../objects";
import Layer from "../classes/Layer";

export const getPointToZoom = (layer: Layer, targetScale: number) => {
  const canvas = layer?.canvas;
  const canvasWidth = canvas?.width || 0;
  const canvasHeight = canvas?.height || 0;

  const objectBounds = layer.getBoundingRect();
  const objectCenterX = objectBounds.left + objectBounds.width / 2;
  const objectCenterY = objectBounds.top + objectBounds.height / 2;

  const targetX = canvasWidth / 2 - objectCenterX * targetScale;
  const targetY = canvasHeight / 2 - objectCenterY * targetScale;

  return { x: targetX, y: targetY, scale: targetScale };
};

const ZOOM_DURATION = 600;
export const zoomToPoint = (
  canvas: FabricCanvas,
  point: { x: number; y: number },
  scale: number,
) => {
  const { x: targetX, y: targetY } = point;
  const targetScale = scale;
  const currentScale = canvas.getZoom();
  const currentX = canvas.viewportTransform[4];
  const currentY = canvas.viewportTransform[5];

  const startTime = performance.now();

  function animateZoom(currentTime: number) {
    const elapsedTime = currentTime - startTime;
    const t = Math.min(elapsedTime / ZOOM_DURATION, 1);
    const easeInOut = t * (2 - t);
    const scale = currentScale + (targetScale - currentScale) * easeInOut;
    const translateX = currentX + (targetX - currentX) * easeInOut;
    const translateY = currentY + (targetY - currentY) * easeInOut;

    canvas.setZoom(scale);
    canvas.viewportTransform[4] = translateX;
    canvas.viewportTransform[5] = translateY;
    canvas.requestRenderAll();

    if (t < 1) {
      requestAnimationFrame(animateZoom);
    }
  }
  requestAnimationFrame(animateZoom);
};

export const centerCanvasViewport = (
  canvas: FabricCanvas,
  layerFit: "none" | "contain" = "none",
) => {
  const clipPath = canvas.clipPath;
  if (clipPath) {
    const zoomX = (canvas.width || 1) / (canvas.clipPath?.width || 1);
    const zoomY = (canvas.height || 1) / (canvas.clipPath?.height || 1);
    const zoom =
      layerFit === "none" ? canvas.getZoom() : Math.min(zoomX, zoomY);
    const panX =
      (canvas.getWidth() / zoom / 2 -
        (clipPath.aCoords?.tl.x || 0) -
        (clipPath.width || 0) / 2) *
      zoom;
    const panY =
      (canvas.getHeight() / zoom / 2 -
        (clipPath.aCoords?.tl.y || 0) -
        (clipPath.height || 0) / 2) *
      zoom;

    canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
    canvas.fire("clippath:modified");
  }
};

export const updateCanvasViewport = (
  canvas: FabricCanvas,
  dimensions: { width: number; height: number },
) => {
  if (!canvas.viewportPoint) {
    canvas.viewportPoint = new FabricPoint(0, 0);
  }
  const viewportPoint = canvas.viewportPoint;
  const dx = dimensions.width - canvas.width;
  const dy = dimensions.height - canvas.height;
  viewportPoint.setXY(Math.round(dx / 2), Math.round(dy / 2));
  canvas.relativePan(viewportPoint);
  canvas.setDimensions(dimensions);
  canvas.fire("clippath:modified");
  canvas.requestRenderAll();
};

export const canvasToBlob = async (
  canvas: HTMLCanvasElement,
): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob));
  });
};

export const canvasToImage = async (
  canvas: FabricCanvas,
): Promise<Blob | null> => {
  const clipPath = canvas.clipPath;
  const layers = canvas.getObjects() as Layer[];
  const canvasElement = document.createElement("canvas");
  const canvasToClone = new FabricCanvas(canvasElement, {
    width: clipPath?.width,
    height: clipPath?.height,
  });
  const clonedLayers = await Promise.all(
    layers.map(async (layer) => layer.clone()),
  );
  canvasToClone.add(...clonedLayers);
  canvasToClone.requestRenderAll();
  return new Promise(async (resolve) => {
    const blob = await canvasToBlob(canvasToClone.toCanvasElement());
    canvasToClone.destroy();
    resolve(blob);
  });
};
